/**
 * Automation Integrations Page
 * Connect/disconnect cards for Microsoft 365, Google, Salesforce, Xero, Square, Calendly, Webhook
 */
import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { useToast } from '../hooks/use-toast';
import { useOrganization } from '../context/OrganizationContext';
import ConfigureOAuthModal from '../components/ConfigureOAuthModal';

import {
  ArrowSquareOut,
  Buildings,
  Calendar,
  ChatText,
  CheckCircle,
  CircleNotch,
  Cloud,
  Envelope,
  FileText,
  GearSix,
  Key,
  LinkBreak,
  LinkSimple,
  Lock,
  PaperPlaneRight,
  Phone,
  Plus,
  Sparkle,
  Star,
  Trash,
  Video,
  WarningCircle
} from '@phosphor-icons/react';
const API_URL = BACKEND_URL;

// Helper to manage header list
const emptyHeader = () => ({ key: '', value: '' });

// Provider configurations with icons
// PUBLIC BETA: Meeting platform integrations are hidden
const PROVIDERS = [
  // ═══════════════════════════════════════════════════════════════════════════
  // RECOMMENDED BOOKING INTEGRATIONS (Top Priority)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'square',
    name: 'Square',
    description: 'Recommended for service businesses (salons, trades, appointments with duration & staff)',
    icon: Calendar,
    color: 'bg-black',
    oauthRequired: true,
    category: 'booking',
    recommended: true,
    actions: ['Create Booking', 'Check Availability', 'Get Staff', 'Get Services']
  },
  {
    id: 'calendly',
    name: 'Calendly',
    description: 'Recommended for professionals (consultants, coaches, advisors)',
    icon: Calendar,
    color: 'bg-blue-600',
    oauthRequired: true,
    category: 'booking',
    recommended: true,
    actions: ['Create Booking', 'Check Availability', 'Get Event Types']
  },
  // Meeting Platforms - DISABLED FOR PUBLIC BETA
  // These will be re-enabled in V1
  /*
  {
    id: 'zoom',
    name: 'Zoom',
    description: 'Connect to Zoom meetings for live note-taking',
    icon: Video,
    color: 'bg-blue-600',
    oauthRequired: true,
    credentialBased: false,
    category: 'meetings',
    actions: ['Join Meeting', 'Live Transcription', 'Meeting Notes']
  },
  {
    id: 'google_meet',
    name: 'Google Meet',
    description: 'Connect to Google Meet calls for live note-taking',
    icon: Video,
    color: 'bg-green-600',
    oauthRequired: true,
    credentialBased: false,
    category: 'meetings',
    actions: ['Join Meeting', 'Live Transcription', 'Meeting Notes']
  },
  {
    id: 'microsoft_teams_meeting',
    name: 'Microsoft Teams',
    description: 'Connect to Teams meetings for live note-taking',
    icon: Video,
    color: 'bg-indigo-600',
    oauthRequired: true,
    credentialBased: false,
    category: 'meetings',
    actions: ['Join Meeting', 'Live Transcription', 'Meeting Notes']
  },
  */
  // Telephony Providers
  {
    id: 'twilio',
    name: 'Twilio',
    description: 'Voice calls and SMS messaging',
    icon: Phone,
    color: 'bg-red-600',
    oauthRequired: false,
    credentialBased: true,
    category: 'telephony',
    actions: ['Send SMS', 'Make Call', 'Receive Calls']
  },
  {
    id: 'telnyx',
    name: 'Telnyx',
    description: 'Voice calls and SMS messaging',
    icon: Phone,
    color: 'bg-emerald-600',
    oauthRequired: false,
    credentialBased: true,
    category: 'telephony',
    actions: ['Send SMS', 'Make Call', 'Receive Calls']
  },
  // Business Apps
  {
    id: 'microsoft_365',
    name: 'Microsoft 365',
    description: 'Connect to Outlook, OneDrive, and Calendar',
    icon: Envelope,
    color: 'bg-blue-500',
    oauthRequired: true,
    category: 'business',
    actions: ['Send Email', 'Create Calendar Event', 'Upload File']
  },
  {
    id: 'google_workspace',
    name: 'Google Workspace',
    description: 'Connect to Gmail and Google Drive',
    icon: Envelope,
    color: 'bg-red-500',
    oauthRequired: true,
    category: 'business',
    actions: ['Send Email', 'Upload to Drive']
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Create leads, tasks, and update records',
    icon: Buildings,
    color: 'bg-sky-500',
    oauthRequired: true,
    category: 'crm',
    actions: ['Create Lead', 'Create Task', 'Update Record']
  },
  {
    id: 'xero',
    name: 'Xero',
    description: 'Create invoices and contacts',
    icon: FileText,
    color: 'bg-teal-500',
    oauthRequired: true,
    category: 'finance',
    actions: ['Create Invoice', 'Create Contact']
  },
  {
    id: 'webhook',
    name: 'Webhook',
    description: 'Send data to any HTTP endpoint',
    icon: PaperPlaneRight,
    color: 'bg-purple-500',
    oauthRequired: false,
    category: 'custom',
    actions: ['POST Request']
  }
];

export default function AutomationIntegrations() {
  const { activeOrganization } = useOrganization();
  const [connections, setConnections] = useState([]);
  const [oauthStatuses, setOauthStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [twilioDialogOpen, setTwilioDialogOpen] = useState(false);
  const [telnyxDialogOpen, setTelnyxDialogOpen] = useState(false);
  const [oauthModalOpen, setOauthModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [webhookConfig, setWebhookConfig] = useState({ 
    url: '', 
    display_name: 'Webhook',
    headers: [emptyHeader()],
    signing_secret: ''
  });
  const [twilioConfig, setTwilioConfig] = useState({
    account_sid: '',
    auth_token: '',
    phone_number: '',
    voice_enabled: true,
    sms_enabled: true
  });
  const [telnyxConfig, setTelnyxConfig] = useState({
    api_key: '',
    phone_number: '',
    voice_enabled: true,
    sms_enabled: true
  });
  const [validatingTwilio, setValidatingTwilio] = useState(false);
  const [twilioValidated, setTwilioValidated] = useState(false);
  const [validatingTelnyx, setValidatingTelnyx] = useState(false);
  const [telnyxValidated, setTelnyxValidated] = useState(false);
  const { toast } = useToast();

  // Helper to get headers with organization context
  const getOrgHeaders = (contentType = true) => {
    const headers = {};
    if (contentType) {
      headers['Content-Type'] = 'application/json';
    }
    if (activeOrganization?.organization_id) {
      headers['X-Organization-Id'] = activeOrganization.organization_id;
    }
    return headers;
  };

  useEffect(() => {
    if (activeOrganization?.organization_id) {
      fetchConnections();
      fetchOAuthStatuses();
    }
  }, [activeOrganization?.organization_id]);

  const fetchConnections = async () => {
    try {
      const response = await fetch(`${API_URL}/api/org/integrations`, {
        credentials: 'include',
        headers: getOrgHeaders(false)
      });
      if (response.ok) {
        const data = await response.json();
        setConnections(data.connections || []);
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOAuthStatuses = async () => {
    const oauthProviders = PROVIDERS.filter(p => p.oauthRequired).map(p => p.id);
    const statuses = {};
    
    for (const providerId of oauthProviders) {
      try {
        const response = await fetch(`${API_URL}/api/org/integrations/${providerId}/oauth-status`, {
          credentials: 'include',
          headers: getOrgHeaders(false)
        });
        if (response.ok) {
          const data = await response.json();
          statuses[providerId] = data;
        }
      } catch (error) {
        console.error(`Error fetching OAuth status for ${providerId}:`, error);
        statuses[providerId] = { configured: false };
      }
    }
    
    setOauthStatuses(statuses);
  };

  const getConnectionForProvider = (providerId) => {
    return connections.find(c => c.provider === providerId);
  };

  const isOAuthConfigured = (providerId) => {
    return oauthStatuses[providerId]?.configured === true;
  };

  const getRedirectUri = (providerId) => {
    return oauthStatuses[providerId]?.redirect_uri || `${API_URL}/api/org/integrations/${providerId}/callback`;
  };

  const handleConfigureOAuth = (provider) => {
    setSelectedProvider(provider.id);
    setOauthModalOpen(true);
  };

  const handleOAuthConfigured = () => {
    fetchOAuthStatuses();
    fetchConnections();
  };

  const [testingConnection, setTestingConnection] = useState(null);

  const handleTestConnection = async (connectionId) => {
    setTestingConnection(connectionId);
    try {
      const response = await fetch(`${API_URL}/api/org/integrations/${connectionId}/test`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(false)
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Build detailed success message
        let description = data.message || 'Integration is working correctly';
        if (data.details) {
          const detailParts = [];
          if (data.details.email) detailParts.push(`Account: ${data.details.email}`);
          if (data.details.account_name && !data.details.email) detailParts.push(`Account: ${data.details.account_name}`);
          if (data.details.tenant_name) detailParts.push(`Org: ${data.details.tenant_name}`);
          if (data.details.org) detailParts.push(`Instance: ${data.details.org}`);
          if (detailParts.length > 0) {
            description = `${data.message} (${detailParts.join(', ')})`;
          }
        }
        
        toast({
          title: '✓ Connection Verified',
          description: description,
          className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
        });
        
        // Refresh connections to update any status changes
        fetchConnections();
      } else {
        // Build helpful error message with suggestion
        const title = data.error_type === 'invalid_token' ? '⚠️ Token Expired' :
                      data.error_type === 'insufficient_scope' ? '⚠️ Permission Issue' :
                      data.error_type === 'timeout' ? '⚠️ Timeout' :
                      data.error_type === 'unreachable' ? '⚠️ Unreachable' :
                      'Connection Issue';
        
        const description = data.suggestion 
          ? `${data.message}. ${data.suggestion}` 
          : data.message || 'Please try reconnecting';
        
        toast({
          title: title,
          description: description,
          variant: 'destructive'
        });
        
        // Refresh to show any attention flags
        fetchConnections();
      }
    } catch (error) {
      console.error('Test connection error:', error);
      toast({
        title: 'Test Failed',
        description: 'Unable to test connection. Please check your network and try again.',
        variant: 'destructive'
      });
    } finally {
      setTestingConnection(null);
    }
  };

  const handleConnect = async (provider) => {
    // Handle provider as either object or string ID
    const providerId = typeof provider === 'string' ? provider : provider.id;
    
    if (providerId === 'webhook') {
      setWebhookDialogOpen(true);
      return;
    }
    
    if (providerId === 'twilio') {
      setTwilioDialogOpen(true);
      return;
    }
    
    if (providerId === 'telnyx') {
      setTelnyxDialogOpen(true);
      return;
    }
    
    // Square and Calendly - initiate OAuth flow
    if (providerId === 'square' || providerId === 'calendly') {
      setConnecting(providerId);
      try {
        const response = await fetch(`${API_URL}/api/integrations/${providerId}/oauth/authorize`, {
          credentials: 'include',
          headers: getOrgHeaders()
        });
        
        const data = await response.json();
        
        if (response.ok && data.auth_url) {
          // Redirect to OAuth provider
          window.location.href = data.auth_url;
        } else {
          toast({
            title: 'OAuth Not Configured',
            description: data.detail || `Please configure ${providerId === 'square' ? 'Square' : 'Calendly'} OAuth credentials first.`,
            variant: 'destructive'
          });
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to initiate OAuth flow',
          variant: 'destructive'
        });
      } finally {
        setConnecting(null);
      }
      return;
    }

    setConnecting(providerId);
    try {
      const response = await fetch(`${API_URL}/api/org/integrations/${providerId}/connect`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders()
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Connection Initiated',
          description: data.message || 'OAuth flow started'
        });
        fetchConnections();
      } else {
        toast({
          title: 'Connection Failed',
          description: data.detail || 'Failed to connect',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to initiate connection',
        variant: 'destructive'
      });
    } finally {
      setConnecting(null);
    }
  };

  const handleConnectWebhook = async () => {
    if (!webhookConfig.url) {
      toast({
        title: 'Error',
        description: 'Webhook URL is required',
        variant: 'destructive'
      });
      return;
    }

    setConnecting('webhook');
    try {
      const response = await fetch(`${API_URL}/api/org/integrations/webhook/connect`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(),
        body: JSON.stringify(webhookConfig)
      });
      
      if (response.ok) {
        toast({
          title: 'Webhook Connected',
          description: webhookConfig.signing_secret 
            ? 'Webhook configured with HMAC signing' 
            : 'Webhook endpoint configured successfully'
        });
        setWebhookDialogOpen(false);
        setWebhookConfig({ url: '', display_name: 'Webhook', headers: [emptyHeader()], signing_secret: '' });
        fetchConnections();
      } else {
        const data = await response.json();
        toast({
          title: 'Failed',
          description: data.detail || 'Failed to connect webhook',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to connect webhook',
        variant: 'destructive'
      });
    } finally {
      setConnecting(null);
    }
  };

  // Twilio validation
  const handleValidateTwilio = async () => {
    if (!twilioConfig.account_sid || !twilioConfig.auth_token) {
      toast({
        title: 'Validation Error',
        description: 'Account SID and Auth Token are required',
        variant: 'destructive'
      });
      return;
    }

    setValidatingTwilio(true);
    try {
      const response = await fetch(`${API_URL}/api/integrations/twilio/validate`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(),
        body: JSON.stringify({
          account_sid: twilioConfig.account_sid,
          auth_token: twilioConfig.auth_token
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setTwilioValidated(true);
        toast({
          title: 'Credentials Valid',
          description: data.account_name ? `Connected to: ${data.account_name}` : 'Twilio credentials verified successfully'
        });
      } else {
        toast({
          title: 'Invalid Credentials',
          description: data.error || 'Please check your Account SID and Auth Token',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Validation Failed',
        description: 'Unable to validate credentials',
        variant: 'destructive'
      });
    } finally {
      setValidatingTwilio(false);
    }
  };

  // Twilio connect
  const handleConnectTwilio = async () => {
    if (!twilioValidated) {
      toast({
        title: 'Validation Required',
        description: 'Please validate your credentials first',
        variant: 'destructive'
      });
      return;
    }

    if (!twilioConfig.phone_number) {
      toast({
        title: 'Phone Number Required',
        description: 'Please enter your Twilio phone number',
        variant: 'destructive'
      });
      return;
    }

    setConnecting('twilio');
    try {
      const response = await fetch(`${API_URL}/api/integrations/twilio/connect`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(),
        body: JSON.stringify(twilioConfig)
      });
      
      if (response.ok) {
        toast({
          title: 'Twilio Connected',
          description: `Phone number ${twilioConfig.phone_number} configured for ${twilioConfig.voice_enabled ? 'Voice' : ''}${twilioConfig.voice_enabled && twilioConfig.sms_enabled ? ' & ' : ''}${twilioConfig.sms_enabled ? 'SMS' : ''}`
        });
        setTwilioDialogOpen(false);
        setTwilioConfig({ account_sid: '', auth_token: '', phone_number: '', voice_enabled: true, sms_enabled: true });
        setTwilioValidated(false);
        fetchConnections();
      } else {
        const data = await response.json();
        toast({
          title: 'Connection Failed',
          description: data.detail || 'Failed to connect Twilio',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to connect Twilio',
        variant: 'destructive'
      });
    } finally {
      setConnecting(null);
    }
  };

  // Telnyx validation
  const handleValidateTelnyx = async () => {
    if (!telnyxConfig.api_key) {
      toast({
        title: 'Validation Error',
        description: 'API Key is required',
        variant: 'destructive'
      });
      return;
    }

    setValidatingTelnyx(true);
    try {
      const response = await fetch(`${API_URL}/api/integrations/telnyx/validate`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(),
        body: JSON.stringify({
          api_key: telnyxConfig.api_key
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setTelnyxValidated(true);
        toast({
          title: 'Credentials Valid',
          description: data.account_name ? `Connected to: ${data.account_name}` : 'Telnyx credentials verified successfully'
        });
      } else {
        toast({
          title: 'Invalid Credentials',
          description: data.error || 'Please check your API Key',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Validation Failed',
        description: 'Unable to validate credentials',
        variant: 'destructive'
      });
    } finally {
      setValidatingTelnyx(false);
    }
  };

  // Telnyx connect
  const handleConnectTelnyx = async () => {
    if (!telnyxValidated) {
      toast({
        title: 'Validation Required',
        description: 'Please validate your credentials first',
        variant: 'destructive'
      });
      return;
    }

    if (!telnyxConfig.phone_number) {
      toast({
        title: 'Phone Number Required',
        description: 'Please enter your Telnyx phone number',
        variant: 'destructive'
      });
      return;
    }

    setConnecting('telnyx');
    try {
      const response = await fetch(`${API_URL}/api/integrations/telnyx/connect`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(),
        body: JSON.stringify(telnyxConfig)
      });
      
      if (response.ok) {
        toast({
          title: 'Telnyx Connected',
          description: `Phone number ${telnyxConfig.phone_number} configured for ${telnyxConfig.voice_enabled ? 'Voice' : ''}${telnyxConfig.voice_enabled && telnyxConfig.sms_enabled ? ' & ' : ''}${telnyxConfig.sms_enabled ? 'SMS' : ''}`
        });
        setTelnyxDialogOpen(false);
        setTelnyxConfig({ api_key: '', phone_number: '', voice_enabled: true, sms_enabled: true });
        setTelnyxValidated(false);
        fetchConnections();
      } else {
        const data = await response.json();
        toast({
          title: 'Connection Failed',
          description: data.detail || 'Failed to connect Telnyx',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to connect Telnyx',
        variant: 'destructive'
      });
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (!window.confirm('Are you sure you want to disconnect this integration?')) {
      return;
    }

    try {
      // Handle Square and Calendly special disconnect endpoints
      let endpoint;
      if (connectionId === 'square') {
        endpoint = `${API_URL}/api/integrations/square/disconnect`;
      } else if (connectionId === 'calendly') {
        endpoint = `${API_URL}/api/integrations/calendly/disconnect`;
      } else {
        endpoint = `${API_URL}/api/org/integrations/${connectionId}/disconnect`;
      }
      
      const response = await fetch(endpoint, {
        method: connectionId === 'square' || connectionId === 'calendly' ? 'DELETE' : 'POST',
        credentials: 'include',
        headers: getOrgHeaders(false)
      });
      
      if (response.ok) {
        toast({
          title: 'Disconnected',
          description: 'Integration disconnected successfully'
        });
        fetchConnections();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <CircleNotch className="w-8 h-8 animate-spin text-[#6366F1]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Automation Integrations</h1>
          <p className="text-gray-400">
            Connect external services to automate workflows when agents complete tasks.
          </p>
        </div>

        {/* Meeting Platforms Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-400" />
            Meeting Platforms
            <span className="text-xs text-gray-500 font-normal ml-2">For Meeting Notes Agent (Connected)</span>
          </h2>
          <TooltipProvider>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
              {PROVIDERS.filter(p => p.category === 'meetings').map((provider) => {
                const connection = getConnectionForProvider(provider.id);
                const isConnected = connection?.status === 'connected';
                const isPending = connection?.status === 'pending';
                const needsAttention = connection?.needs_attention === true;
                const oauthConfigured = isOAuthConfigured(provider.id);
                const needsOAuthConfig = provider.oauthRequired && !oauthConfigured;
                const Icon = provider.icon;

                return (
                  <Card key={provider.id} className="bg-[#111111] border-[rgba(255,255,255,0.1)] flex flex-col h-full">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between h-10">
                        <div className={`p-2 rounded-lg ${provider.color}`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="h-5 flex items-center">
                          {isConnected && needsAttention ? (
                            <div className="flex items-center gap-1 text-orange-400 text-xs">
                              <WarningCircle className="w-3 h-3" />
                              Needs Attention
                            </div>
                          ) : isConnected ? (
                            <div className="flex items-center gap-1 text-green-400 text-xs">
                              <CheckCircle className="w-3 h-3" />
                              Connected
                            </div>
                          ) : oauthConfigured && provider.oauthRequired ? (
                            <div className="flex items-center gap-1 text-blue-400 text-xs">
                              <Key className="w-3 h-3" />
                              Configured
                            </div>
                          ) : needsOAuthConfig ? (
                            <div className="flex items-center gap-1 text-yellow-400 text-xs">
                              <Lock className="w-3 h-3" />
                              Setup Required
                            </div>
                          ) : isPending ? (
                            <div className="flex items-center gap-1 text-yellow-400 text-xs">
                              <WarningCircle className="w-3 h-3" />
                              Pending
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <CardTitle className="text-white text-lg mt-3 h-7">{provider.name}</CardTitle>
                      <CardDescription className="text-gray-400 text-sm h-10 line-clamp-2">
                        {provider.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1">
                      <div className="mb-4 h-16">
                        <p className="text-xs text-gray-500 mb-2">Capabilities:</p>
                        <div className="flex flex-wrap gap-1">
                          {provider.actions.map((action, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-[rgba(255,255,255,0.05)] rounded text-xs text-gray-400">
                              {action}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex-1"></div>
                      {isConnected && connection?.masked_account && (
                        <div className="mb-4 p-2 bg-[rgba(255,255,255,0.03)] rounded">
                          <p className="text-xs text-gray-500">Connected as:</p>
                          <p className="text-sm text-gray-300 truncate">{connection.masked_account}</p>
                        </div>
                      )}
                      <div className="h-24 flex flex-col justify-end">
                        <div className="flex gap-2">
                          {isConnected ? (
                            <>
                              <Button variant="outline" size="sm" className="flex-1 border-[rgba(255,255,255,0.1)] text-gray-300 hover:bg-[rgba(255,255,255,0.05)]"
                                onClick={() => handleTestConnection(connection.id)} disabled={testingConnection === connection.id}>
                                {testingConnection === connection.id ? <CircleNotch className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                                Test
                              </Button>
                              <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDisconnect(connection.id)}>
                                <LinkBreak className="w-3 h-3" />
                              </Button>
                            </>
                          ) : needsOAuthConfig ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="sm" className="flex-1 bg-[#6366F1] hover:bg-[#5558E3]" onClick={() => handleConfigureOAuth(provider)}>
                                  <Key className="w-3 h-3 mr-1" />
                                  Configure OAuth
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)] text-white max-w-xs">
                                <p className="text-sm">This integration requires OAuth credentials.</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Button size="sm" className="flex-1 bg-[#6366F1] hover:bg-[#5558E3]" onClick={() => handleConnect(provider)} disabled={connecting === provider.id}>
                              {connecting === provider.id ? <CircleNotch className="w-3 h-3 mr-1 animate-spin" /> : <LinkSimple className="w-3 h-3 mr-1" />}
                              Connect
                            </Button>
                          )}
                        </div>
                        <div className="h-6 mt-3">
                          {provider.oauthRequired && !isConnected && (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              {oauthConfigured ? (
                                <>
                                  <CheckCircle className="w-3 h-3 text-green-400" />
                                  <span className="text-green-400">OAuth configured</span>
                                  <span className="text-gray-600 mx-1">•</span>
                                  <button className="text-gray-400 hover:text-white" onClick={() => handleConfigureOAuth(provider)}>Reconfigure</button>
                                </>
                              ) : (
                                <>
                                  <Lock className="w-3 h-3" />
                                  Requires OAuth credentials
                                </>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TooltipProvider>
        </div>

        {/* Booking & Scheduling Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#6366F1]" />
            Booking & Scheduling
            <span className="px-2 py-0.5 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] rounded-full text-[10px] font-semibold text-white ml-2">
              Recommended for Voice Agents
            </span>
          </h2>
          <TooltipProvider>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
              {PROVIDERS.filter(p => p.category === 'booking').map((provider) => {
                const connection = getConnectionForProvider(provider.id);
                const isConnected = connection?.status === 'connected';
                const isPending = connection?.status === 'pending';
                const needsAttention = connection?.needs_attention === true;
                const oauthConfigured = isOAuthConfigured(provider.id);
                const needsOAuthConfig = provider.oauthRequired && !oauthConfigured;
                const Icon = provider.icon;
                const isComingSoon = provider.comingSoon === true;
                const isRecommended = provider.recommended === true;

                return (
                  <Card key={provider.id} className={`bg-[#111111] border-[rgba(255,255,255,0.1)] flex flex-col h-full ${isRecommended ? 'ring-1 ring-[#6366F1]/30' : ''}`}>
                    <CardHeader className="pb-3">
                      {/* Row 1: Icon and Status Badge */}
                      <div className="flex items-start justify-between h-10">
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg ${provider.color}`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          {isRecommended && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] rounded-full text-[10px] font-semibold text-white">
                              <Star className="w-3 h-3" />
                              Recommended
                            </span>
                          )}
                        </div>
                        {/* Status Badge */}
                        <div className="h-5 flex items-center">
                          {isComingSoon ? (
                            <div className="flex items-center gap-1 text-purple-400 text-xs">
                              <Sparkle className="w-3 h-3" />
                              Coming Soon
                            </div>
                          ) : isConnected ? (
                            <div className="flex items-center gap-1 text-green-400 text-xs">
                              <CheckCircle className="w-3 h-3" />
                              Connected
                            </div>
                          ) : oauthConfigured && provider.oauthRequired ? (
                            <div className="flex items-center gap-1 text-blue-400 text-xs">
                              <Key className="w-3 h-3" />
                              Configured
                            </div>
                          ) : needsOAuthConfig ? (
                            <div className="flex items-center gap-1 text-yellow-400 text-xs">
                              <Lock className="w-3 h-3" />
                              Setup Required
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <CardTitle className="text-white text-lg mt-3 h-7">{provider.name}</CardTitle>
                      <CardDescription className="text-gray-400 text-sm h-10 line-clamp-2">
                        {provider.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1">
                      <div className="mb-4 h-16">
                        <p className="text-xs text-gray-500 mb-2">Available Actions:</p>
                        <div className="flex flex-wrap gap-1">
                          {provider.actions.map((action, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 bg-[rgba(255,255,255,0.05)] rounded text-xs text-gray-400"
                            >
                              {action}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex-1"></div>
                      
                      {/* Buttons */}
                      <div className="flex gap-2 mt-4">
                        {isComingSoon ? (
                          <Button
                            size="sm"
                            className="flex-1 bg-gray-600/50 cursor-not-allowed"
                            disabled={true}
                          >
                            <Sparkle className="w-3 h-3 mr-1" />
                            Coming Soon
                          </Button>
                        ) : needsOAuthConfig ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                className="flex-1 bg-[#6366F1] hover:bg-[#5558E3]"
                                onClick={() => handleConfigureOAuth(provider)}
                              >
                                <Key className="w-3 h-3 mr-1" />
                                Configure OAuth
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)] text-white max-w-xs">
                              <p className="text-sm">
                                This integration requires OAuth credentials from {provider.name}.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : isConnected ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                            onClick={() => handleDisconnect(provider.id)}
                            disabled={connecting === provider.id}
                          >
                            <LinkBreak className="w-3 h-3 mr-1" />
                            Disconnect
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="flex-1 bg-[#6366F1] hover:bg-[#5558E3]"
                            onClick={() => handleConnect(provider.id)}
                            disabled={connecting === provider.id}
                          >
                            {connecting === provider.id ? (
                              <CircleNotch className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <LinkSimple className="w-3 h-3 mr-1" />
                            )}
                            Connect
                          </Button>
                        )}
                        
                        {oauthConfigured && !isComingSoon && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[rgba(255,255,255,0.1)] text-gray-400 hover:text-white"
                            onClick={() => handleConfigureOAuth(provider)}
                          >
                            <GearSix className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TooltipProvider>
        </div>

        {/* Other Integrations Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <GearSix className="w-5 h-5 text-purple-400" />
            Telephony & Business Apps
          </h2>
        <TooltipProvider>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            {PROVIDERS.filter(p => p.category !== 'meetings' && p.category !== 'booking').map((provider) => {
              const connection = getConnectionForProvider(provider.id);
              const isConnected = connection?.status === 'connected';
              const isPending = connection?.status === 'pending';
              const needsAttention = connection?.needs_attention === true;
              const oauthConfigured = isOAuthConfigured(provider.id);
              const needsOAuthConfig = provider.oauthRequired && !oauthConfigured;
              const Icon = provider.icon;
              const isComingSoon = provider.comingSoon === true;
              const isRecommended = provider.recommended === true;

              return (
                <Card key={provider.id} className={`bg-[#111111] border-[rgba(255,255,255,0.1)] flex flex-col h-full ${isRecommended ? 'ring-1 ring-[#6366F1]/30' : ''}`}>
                  <CardHeader className="pb-3">
                    {/* Row 1: Icon and Status Badge */}
                    <div className="flex items-start justify-between h-10">
                      <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${provider.color}`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        {isRecommended && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] rounded-full text-[10px] font-semibold text-white">
                            <Star className="w-3 h-3" />
                            Recommended
                          </span>
                        )}
                      </div>
                      {/* Status Badge - fixed height container */}
                      <div className="h-5 flex items-center">
                        {isComingSoon ? (
                          <div className="flex items-center gap-1 text-purple-400 text-xs">
                            <Sparkle className="w-3 h-3" />
                            Coming Soon
                          </div>
                        ) : isConnected && needsAttention ? (
                          <div className="flex items-center gap-1 text-orange-400 text-xs">
                            <WarningCircle className="w-3 h-3" />
                            Needs Attention
                          </div>
                        ) : isConnected ? (
                          <div className="flex items-center gap-1 text-green-400 text-xs">
                            <CheckCircle className="w-3 h-3" />
                            Connected
                          </div>
                        ) : oauthConfigured && provider.oauthRequired ? (
                          <div className="flex items-center gap-1 text-blue-400 text-xs">
                            <Key className="w-3 h-3" />
                            Configured
                          </div>
                        ) : needsOAuthConfig ? (
                          <div className="flex items-center gap-1 text-yellow-400 text-xs">
                            <Lock className="w-3 h-3" />
                            Setup Required
                          </div>
                        ) : isPending ? (
                          <div className="flex items-center gap-1 text-yellow-400 text-xs">
                            <WarningCircle className="w-3 h-3" />
                            Pending
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {/* Row 2: Title - fixed height */}
                    <CardTitle className="text-white text-lg mt-3 h-7">{provider.name}</CardTitle>
                    {/* Row 3: Description - fixed height */}
                    <CardDescription className="text-gray-400 text-sm h-10 line-clamp-2">
                      {provider.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1">
                    {/* Available Actions - fixed height section */}
                    <div className="mb-4 h-16">
                      <p className="text-xs text-gray-500 mb-2">Available Actions:</p>
                      <div className="flex flex-wrap gap-1">
                        {provider.actions.map((action, idx) => (
                          <span 
                            key={idx}
                            className="px-2 py-0.5 bg-[rgba(255,255,255,0.05)] rounded text-xs text-gray-400"
                          >
                            {action}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Spacer to push buttons to bottom */}
                    <div className="flex-1"></div>

                    {/* Connected Account Info - only shown when connected */}
                    {isConnected && connection?.masked_account && (
                      <div className="mb-4 p-2 bg-[rgba(255,255,255,0.03)] rounded">
                        <p className="text-xs text-gray-500">Connected as:</p>
                        <p className="text-sm text-gray-300 truncate">{connection.masked_account}</p>
                      </div>
                    )}

                    {/* Webhook URLs - Only for Twilio/Telnyx when connected */}
                    {isConnected && (provider.id === 'twilio' || provider.id === 'telnyx') && (
                      <div className="mb-4 p-2 bg-[rgba(99,102,241,0.05)] rounded-lg border border-[rgba(99,102,241,0.1)]">
                        <p className="text-[10px] text-[#6366F1] font-medium mb-1.5 flex items-center gap-1">
                          <GearSix className="w-3 h-3" />
                          Webhook URLs
                        </p>
                        <div className="space-y-1 text-[9px]">
                          <div className="flex items-start gap-1">
                            <span className="text-gray-500 shrink-0 w-10">Voice:</span>
                            <code className="text-gray-300 bg-black/30 px-1 py-0.5 rounded break-all leading-tight select-all">
                              .../twilio/inbound/voice
                            </code>
                          </div>
                          <div className="flex items-start gap-1">
                            <span className="text-gray-500 shrink-0 w-10">SMS:</span>
                            <code className="text-gray-300 bg-black/30 px-1 py-0.5 rounded break-all leading-tight select-all">
                              .../twilio/inbound/sms
                            </code>
                          </div>
                        </div>
                        <p className="text-[8px] text-gray-500 mt-1.5">
                          Full URLs shown in config dialog
                        </p>
                      </div>
                    )}

                    {/* Action Buttons and Footer - fixed height section */}
                    <div className="h-24 flex flex-col justify-end">
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        {isConnected ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 border-[rgba(255,255,255,0.1)] text-gray-300 hover:bg-[rgba(255,255,255,0.05)]"
                              onClick={() => handleTestConnection(connection.id)}
                              disabled={testingConnection === connection.id}
                            >
                              {testingConnection === connection.id ? (
                                <CircleNotch className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <CheckCircle className="w-3 h-3 mr-1" />
                              )}
                              Test
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              onClick={() => handleDisconnect(connection.id)}
                            >
                              <LinkBreak className="w-3 h-3" />
                            </Button>
                          </>
                        ) : isComingSoon ? (
                          /* Coming Soon - show placeholder button */
                          <Button
                            size="sm"
                            className="flex-1 bg-gray-600/50 cursor-not-allowed"
                            disabled={true}
                          >
                            <Sparkle className="w-3 h-3 mr-1" />
                            Coming Soon
                          </Button>
                        ) : needsOAuthConfig ? (
                          /* OAuth not configured - show Configure OAuth button with consistent purple style */
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                className="flex-1 bg-[#6366F1] hover:bg-[#5558E3]"
                                onClick={() => handleConfigureOAuth(provider)}
                              >
                                <Key className="w-3 h-3 mr-1" />
                                Configure OAuth
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)] text-white max-w-xs">
                              <p className="text-sm">
                                This integration requires OAuth credentials to be configured before connecting.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          /* OAuth configured or webhook - show Connect button */
                          <Button
                            size="sm"
                            className="flex-1 bg-[#6366F1] hover:bg-[#5558E3]"
                            onClick={() => handleConnect(provider)}
                            disabled={connecting === provider.id}
                          >
                            {connecting === provider.id ? (
                              <CircleNotch className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <LinkSimple className="w-3 h-3 mr-1" />
                            )}
                            Connect
                          </Button>
                        )}
                      </div>

                      {/* OAuth Configuration Status - fixed height placeholder */}
                      <div className="h-6 mt-3">
                        {provider.oauthRequired && !isConnected && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            {oauthConfigured ? (
                              <>
                                <CheckCircle className="w-3 h-3 text-green-400" />
                                <span className="text-green-400">OAuth configured</span>
                                <span className="text-gray-600 mx-1">•</span>
                                <button 
                                  className="text-gray-400 hover:text-white"
                                  onClick={() => handleConfigureOAuth(provider)}
                                >
                                  Reconfigure
                                </button>
                              </>
                            ) : (
                              <>
                                <Lock className="w-3 h-3" />
                                Requires OAuth credentials
                              </>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TooltipProvider>
        </div>

        {/* Info Section */}
        <div className="mt-8 p-4 bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.3)] rounded-lg">
          <h3 className="text-[#6366F1] font-medium mb-2">Setting Up Integrations</h3>
          <p className="text-sm text-gray-400">
            OAuth integrations (Microsoft 365, Google, Salesforce, Xero, Square, Calendly) require credentials from each provider. 
            Click <strong>Configure OAuth</strong> on any integration to securely store your credentials. 
            Webhooks can be connected immediately without OAuth.
          </p>
        </div>
      </div>

      {/* Webhook Configuration Dialog */}
      <Dialog open={webhookDialogOpen} onOpenChange={setWebhookDialogOpen}>
        <DialogContent className="bg-[#111111] border-[rgba(255,255,255,0.1)] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Configure Webhook</DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure the webhook endpoint and optional security settings.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Display Name</Label>
              <Input
                value={webhookConfig.display_name}
                onChange={(e) => setWebhookConfig(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="My Webhook"
                className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Webhook URL *</Label>
              <Input
                value={webhookConfig.url}
                onChange={(e) => setWebhookConfig(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://your-endpoint.com/webhook"
                className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white"
              />
            </div>
            
            {/* Custom Headers */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-300">Custom Headers (Optional)</Label>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setWebhookConfig(prev => ({ 
                    ...prev, 
                    headers: [...prev.headers, emptyHeader()] 
                  }))}
                  className="text-[#6366F1] h-6 px-2"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {webhookConfig.headers.map((header, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      value={header.key}
                      onChange={(e) => {
                        const newHeaders = [...webhookConfig.headers];
                        newHeaders[idx] = { ...header, key: e.target.value };
                        setWebhookConfig(prev => ({ ...prev, headers: newHeaders }));
                      }}
                      placeholder="Header name"
                      className="flex-1 bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white text-sm"
                    />
                    <Input
                      value={header.value}
                      onChange={(e) => {
                        const newHeaders = [...webhookConfig.headers];
                        newHeaders[idx] = { ...header, value: e.target.value };
                        setWebhookConfig(prev => ({ ...prev, headers: newHeaders }));
                      }}
                      placeholder="Value"
                      className="flex-1 bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white text-sm"
                    />
                    {webhookConfig.headers.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          const newHeaders = webhookConfig.headers.filter((_, i) => i !== idx);
                          setWebhookConfig(prev => ({ ...prev, headers: newHeaders }));
                        }}
                        className="text-red-400 h-8 px-2"
                      >
                        <Trash className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Signing Secret */}
            <div className="p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
              <Label className="text-gray-300">Signing Secret (Optional)</Label>
              <p className="text-xs text-gray-500 mb-2">
                If provided, payloads will be signed with HMAC SHA-256. 
                Signature sent in <code className="text-[#6366F1]">X-MAM-Signature</code> header.
              </p>
              <Input
                type="password"
                value={webhookConfig.signing_secret}
                onChange={(e) => setWebhookConfig(prev => ({ ...prev, signing_secret: e.target.value }))}
                placeholder="Enter signing secret..."
                className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setWebhookDialogOpen(false)}
              className="border-[rgba(255,255,255,0.1)] text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConnectWebhook}
              disabled={connecting === 'webhook'}
              className="bg-[#6366F1] hover:bg-[#5558E3]"
            >
              {connecting === 'webhook' ? (
                <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Connect Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Twilio Configuration Dialog */}
      <Dialog open={twilioDialogOpen} onOpenChange={setTwilioDialogOpen}>
        <DialogContent className="bg-[#111111] border-[rgba(255,255,255,0.1)] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Phone className="w-5 h-5 text-red-500" />
              Configure Twilio
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Connect your Twilio account to enable voice calls and SMS messaging.
              <a 
                href="https://console.twilio.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#6366F1] hover:underline ml-1 inline-flex items-center gap-1"
              >
                Get credentials <ArrowSquareOut className="w-3 h-3" />
              </a>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Account SID *</Label>
              <Input
                value={twilioConfig.account_sid}
                onChange={(e) => {
                  setTwilioConfig(prev => ({ ...prev, account_sid: e.target.value }));
                  setTwilioValidated(false);
                }}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white font-mono text-sm"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">Auth Token *</Label>
              <Input
                type="password"
                value={twilioConfig.auth_token}
                onChange={(e) => {
                  setTwilioConfig(prev => ({ ...prev, auth_token: e.target.value }));
                  setTwilioValidated(false);
                }}
                placeholder="Your auth token"
                className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white font-mono text-sm"
              />
            </div>

            {/* Validate Button */}
            <Button
              onClick={handleValidateTwilio}
              disabled={validatingTwilio || !twilioConfig.account_sid || !twilioConfig.auth_token}
              className={`w-full ${twilioValidated ? 'bg-green-600 hover:bg-green-700' : 'bg-[#6366F1] hover:bg-[#5558E3]'}`}
            >
              {validatingTwilio ? (
                <><CircleNotch className="w-4 h-4 mr-2 animate-spin" /> Validating...</>
              ) : twilioValidated ? (
                <><CheckCircle className="w-4 h-4 mr-2" /> Credentials Validated</>
              ) : (
                'Validate Credentials'
              )}
            </Button>

            {twilioValidated && (
              <>
                <div className="border-t border-[rgba(255,255,255,0.1)] pt-4">
                  <Label className="text-gray-300">Twilio Phone Number *</Label>
                  <Input
                    value={twilioConfig.phone_number}
                    onChange={(e) => setTwilioConfig(prev => ({ ...prev, phone_number: e.target.value }))}
                    placeholder="+14155551234"
                    className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">E.164 format (e.g., +14155551234)</p>
                </div>

                {/* Webhook URLs to configure in Twilio */}
                <div className="p-4 bg-[rgba(99,102,241,0.05)] rounded-lg border border-[rgba(99,102,241,0.2)]">
                  <p className="text-sm text-[#6366F1] font-medium mb-3 flex items-center gap-2">
                    <GearSix className="w-4 h-4" />
                    Configure these Webhook URLs in Twilio Console
                  </p>
                  <div className="space-y-3 text-xs">
                    <div>
                      <span className="text-gray-400 block mb-1">Voice Webhook (A CALL COMES IN):</span>
                      <code className="text-white bg-black/40 px-2 py-1 rounded block overflow-x-auto select-all">
                        {API_URL}/api/integrations/twilio/inbound/voice
                      </code>
                    </div>
                    <div>
                      <span className="text-gray-400 block mb-1">SMS Webhook (A MESSAGE COMES IN):</span>
                      <code className="text-white bg-black/40 px-2 py-1 rounded block overflow-x-auto select-all">
                        {API_URL}/api/integrations/twilio/inbound/sms
                      </code>
                    </div>
                    <div>
                      <span className="text-gray-400 block mb-1">Status Callback:</span>
                      <code className="text-white bg-black/40 px-2 py-1 rounded block overflow-x-auto select-all">
                        {API_URL}/api/integrations/twilio/status
                      </code>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-3">
                    Set these in Twilio Console → Phone Numbers → Your Number → Configure
                  </p>
                </div>

                {/* Feature Toggles */}
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={twilioConfig.voice_enabled}
                      onChange={(e) => setTwilioConfig(prev => ({ ...prev, voice_enabled: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-600 text-[#6366F1] focus:ring-[#6366F1]"
                    />
                    <div>
                      <span className="text-white text-sm font-medium">Voice Calls</span>
                      <p className="text-xs text-gray-500">Inbound/outbound calls</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={twilioConfig.sms_enabled}
                      onChange={(e) => setTwilioConfig(prev => ({ ...prev, sms_enabled: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-600 text-[#6366F1] focus:ring-[#6366F1]"
                    />
                    <div>
                      <span className="text-white text-sm font-medium">SMS</span>
                      <p className="text-xs text-gray-500">PaperPlaneRight/receive messages</p>
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setTwilioDialogOpen(false);
                setTwilioConfig({ account_sid: '', auth_token: '', phone_number: '', voice_enabled: true, sms_enabled: true });
                setTwilioValidated(false);
              }}
              className="border-[rgba(255,255,255,0.1)] text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConnectTwilio}
              disabled={connecting === 'twilio' || !twilioValidated || !twilioConfig.phone_number}
              className="bg-red-600 hover:bg-red-700"
            >
              {connecting === 'twilio' ? (
                <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Phone className="w-4 h-4 mr-2" />
              )}
              Connect Twilio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Telnyx Configuration Dialog */}
      <Dialog open={telnyxDialogOpen} onOpenChange={setTelnyxDialogOpen}>
        <DialogContent className="bg-[#111111] border-[rgba(255,255,255,0.1)] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Phone className="w-5 h-5 text-emerald-500" />
              Configure Telnyx
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Connect your Telnyx account to enable voice calls and SMS messaging.
              <a 
                href="https://portal.telnyx.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#6366F1] hover:underline ml-1 inline-flex items-center gap-1"
              >
                Get credentials <ArrowSquareOut className="w-3 h-3" />
              </a>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">API Key *</Label>
              <Input
                type="password"
                value={telnyxConfig.api_key}
                onChange={(e) => {
                  setTelnyxConfig(prev => ({ ...prev, api_key: e.target.value }));
                  setTelnyxValidated(false);
                }}
                placeholder="KEY_xxxxxxxxxxxxxxxxxxxxxxxxx"
                className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Found in Telnyx Portal → API Keys</p>
            </div>

            {/* Validate Button */}
            <Button
              onClick={handleValidateTelnyx}
              disabled={validatingTelnyx || !telnyxConfig.api_key}
              className={`w-full ${telnyxValidated ? 'bg-green-600 hover:bg-green-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              {validatingTelnyx ? (
                <><CircleNotch className="w-4 h-4 mr-2 animate-spin" /> Validating...</>
              ) : telnyxValidated ? (
                <><CheckCircle className="w-4 h-4 mr-2" /> Credentials Validated</>
              ) : (
                'Validate Credentials'
              )}
            </Button>

            {telnyxValidated && (
              <>
                <div className="border-t border-[rgba(255,255,255,0.1)] pt-4">
                  <Label className="text-gray-300">Telnyx Phone Number *</Label>
                  <Input
                    value={telnyxConfig.phone_number}
                    onChange={(e) => setTelnyxConfig(prev => ({ ...prev, phone_number: e.target.value }))}
                    placeholder="+14155551234"
                    className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">E.164 format (e.g., +14155551234)</p>
                </div>

                {/* Webhook URLs to configure in Telnyx */}
                <div className="p-4 bg-[rgba(16,185,129,0.05)] rounded-lg border border-[rgba(16,185,129,0.2)]">
                  <p className="text-sm text-emerald-400 font-medium mb-3 flex items-center gap-2">
                    <GearSix className="w-4 h-4" />
                    Configure these Webhook URLs in Telnyx Portal
                  </p>
                  <div className="space-y-3 text-xs">
                    <div>
                      <span className="text-gray-400 block mb-1">Voice Webhook URL:</span>
                      <code className="text-white bg-black/40 px-2 py-1 rounded block overflow-x-auto select-all">
                        {API_URL}/api/integrations/telnyx/inbound/voice
                      </code>
                    </div>
                    <div>
                      <span className="text-gray-400 block mb-1">SMS Webhook URL:</span>
                      <code className="text-white bg-black/40 px-2 py-1 rounded block overflow-x-auto select-all">
                        {API_URL}/api/integrations/telnyx/inbound/sms
                      </code>
                    </div>
                    <div>
                      <span className="text-gray-400 block mb-1">Voice Events / Status Callback:</span>
                      <code className="text-white bg-black/40 px-2 py-1 rounded block overflow-x-auto select-all">
                        {API_URL}/api/integrations/telnyx/status-callback
                      </code>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-3">
                    Set these in Telnyx Portal → Messaging Profiles & Voice → TeXML Applications
                  </p>
                </div>

                {/* Feature Toggles */}
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={telnyxConfig.voice_enabled}
                      onChange={(e) => setTelnyxConfig(prev => ({ ...prev, voice_enabled: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500"
                    />
                    <div>
                      <span className="text-white text-sm font-medium">Voice Calls</span>
                      <p className="text-xs text-gray-500">Inbound/outbound calls</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={telnyxConfig.sms_enabled}
                      onChange={(e) => setTelnyxConfig(prev => ({ ...prev, sms_enabled: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500"
                    />
                    <div>
                      <span className="text-white text-sm font-medium">SMS</span>
                      <p className="text-xs text-gray-500">PaperPlaneRight/receive messages</p>
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setTelnyxDialogOpen(false);
                setTelnyxConfig({ api_key: '', phone_number: '', voice_enabled: true, sms_enabled: true });
                setTelnyxValidated(false);
              }}
              className="border-[rgba(255,255,255,0.1)] text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConnectTelnyx}
              disabled={connecting === 'telnyx' || !telnyxValidated || !telnyxConfig.phone_number}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {connecting === 'telnyx' ? (
                <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Phone className="w-4 h-4 mr-2" />
              )}
              Connect Telnyx
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configure OAuth Modal */}
      <ConfigureOAuthModal
        open={oauthModalOpen}
        onOpenChange={setOauthModalOpen}
        provider={selectedProvider}
        redirectUri={selectedProvider ? getRedirectUri(selectedProvider) : ''}
        onConfigured={handleOAuthConfigured}
        organizationId={activeOrganization?.organization_id}
        isConfigured={selectedProvider ? isOAuthConfigured(selectedProvider) : false}
      />
    </div>
  );
}
